<!--

How to use this class:
  the word 'option' describes in this context the object you can select in the dropdown, when you type something in the input field.

  props:
    optionsFetch -> function that returns a promise with the options the user can select
    value -> the selected value of the field, can be null
    optionText -> the property of the option that should be displayed as text
    optionValue -> the property of the option that should be used as value, often the id
    label -> the label of the form field
    placeholder -> the placeholder of the input field

  emits:
    input -> the selected value. It is the optionValue of the selected option, which was fetched by the optionsFetch function.

-->

<template>
  <v-autocomplete
    @click="$emit('click')"
    filled
    v-model="selectedOption"
    :items="options"
    :loading="isLoading"
    :search-input.sync="search"
    hide-no-data
    hide-selected
    :item-text="optionText"
    :item-value="optionValue"
    :label="label"
    :placeholder="placeholder"
    :rules="rules"
    :return-object="returnObject"
    :disabled="disabled"
    :item-disabled="isItemDisabled"
    :filter="() => true"
  >
    <template v-slot:append>
      <slot name="append"></slot>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from "vue"

type OptionsFetchFunction = (query: string) => Promise<any[]>

export default defineComponent({
  name: "AutocompleteFormField",
  props: {
    optionsFetch: {
      type: Function as PropType<OptionsFetchFunction>,
      default: () => [],
    },
    value: { type: [Object, String] as PropType<any | null>, default: null },
    optionText: { type: String },
    optionValue: { type: String },
    label: { type: String },
    placeholder: { type: String },
    rules: { type: Array as PropType<((v: any) => boolean | string)[]> },
    disabled: { type: Boolean, default: false },
    selectedItems: { type: Array as PropType<any[]>, default: () => [] },
    returnObject: { type: Boolean, default: false },
  },
  data() {
    return {
      search: "",
      options: [this.value],
      isLoading: false,
      debounceTimer: null,
    }
  },
  methods: {
    isItemDisabled(item: any): boolean {
      if (!this.selectedItems) return false
      return this.selectedItems.some(
        (selectedItem) =>
          selectedItem[this.optionValue] === item[this.optionValue]
      )
    },
  },
  watch: {
    search: async function (query: string) {
      if (this.disabled) return
      if (query && query.length > 0) {
        clearTimeout(this.debounceTimer)
        this.debounceTimer = setTimeout(async () => {
          this.isLoading = true
          this.options = [
            ...(await this.optionsFetch(query)),
            ...(this.value ? [this.value] : []),
          ]
          this.isLoading = false
        }, 500)
      }
    },
    value: function (value: any) {
      this.options = [value]
    },
  },
  setup(props, { emit }) {
    const _selectedOption = ref(props.value)
    watch(
      () => props.value,
      (value) => {
        _selectedOption.value = value
      }
    )
    const selectedOption = computed({
      get: () => _selectedOption.value,
      set: (value) => {
        _selectedOption.value = value
        emit("input", value)
      },
    })

    return { selectedOption }
  },
})
</script>
